import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  TextField,
  Typography,
  Switch,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  updateEstimatorHook,
  getMyEstimatorHookDetail,
} from "../../features/estimator-hook-module/estimatorHookAction";
import { resetEstimatorHookState } from "../../features/estimator-hook-module/estimatorHookSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../stores";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "95vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: "85vh",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "32px",
  fontWeight: 700,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: "20px",
  fontWeight: 600,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const CopyButton = styled(Button)(({ theme }) => ({
  minWidth: "unset",
  padding: theme.spacing(1),
  background: "#455354",
  color: "#fff",
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s ease",
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  "&:hover": {
    backgroundColor: "#5a6a6c",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    transform: "translateY(-1px)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75),
  },
}));

const EmbedCodeContainer = styled(Box)(({ theme }) => ({
  background: "#f5f5f5",
  padding: theme.spacing(2),
  borderRadius: "8px",
  position: "relative",
  marginBottom: theme.spacing(2),
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  overflow: "auto",
  maxHeight: "200px",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
    maxHeight: "150px",
  },
}));

const EmbedCode = styled("pre")(({ theme }) => ({
  margin: 0,
  padding: "20px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "'Roboto Mono', monospace",
  fontSize: "14px",
  color: "#4b5b5d",
  background: "transparent",
  [theme.breakpoints.down("sm")]: {
    padding: "5px",
    fontSize: "12px",
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const EstimatorHook: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const { estimatorHook, loading, error } = useSelector(
    (state: any) => state.estimatorHook
  );

  const defaultWebsite = window.location.origin;
  const [website, setWebsite] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [clientId, setClientId] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    dispatch(getMyEstimatorHookDetail());
  }, [dispatch]);

  useEffect(() => {
    if (estimatorHook) {
      setWebsite(estimatorHook.website || defaultWebsite);
      setEnabled(estimatorHook.enabled || false);
      setClientId(estimatorHook.clientId || "");
      setWebsiteError("");
      setWebsiteTouched(false);
    }
  }, [estimatorHook, defaultWebsite]);

  useEffect(() => {
    if (error) {
      setWebsite(defaultWebsite);
      setEnabled(false);
      setClientId("");
      setWebsiteError("");
      setWebsiteTouched(false);
    }
  }, [error, defaultWebsite]);

  useEffect(() => {
    return () => {
      dispatch(resetEstimatorHookState());
    };
  }, [dispatch]);

  const validateWebsite = (url: string): string | undefined => {
    if (!url) return "Website URL is required";
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;
    if (!urlPattern.test(url)) return "Please enter a valid URL";
    return undefined;
  };

  const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWebsite = e.target.value;
    setWebsite(newWebsite);
    setWebsiteTouched(true);
    setWebsiteError(validateWebsite(newWebsite) || "");
  };

  const handleWebsiteBlur = () => {
    setWebsiteTouched(true);
    setWebsiteError(validateWebsite(website) || "");
  };

  const generateEmbedCode = () => {
    const baseUrl = "https://rightsite.nyxwolves.com";
    return `<iframe width="100%" height="100%" class="embed-container" src="${baseUrl}/embed/${clientId}" title="Embedded Estimator" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generateEmbedCode());
      setIsCopied(true);
      toast.success("Embed code copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy code");
    }
  };

  const handleToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = e.target.checked;

    // ---------- Validation when turning ON ----------
    if (newEnabled) {
      if (!website) {
        toast.error("Enter the Domain URL");
        setWebsiteError("Domain URL is required");
        setWebsiteTouched(true);
        return;
      }
      if (websiteError) {
        toast.error("Please enter a valid Domain URL");
        return;
      }
    }

    let newClientId = clientId;

    // ---- NEW CLIENT ID LOGIC ----
    if (newEnabled) {
      newClientId = crypto.randomUUID();
      setClientId(newClientId);
    }

    setEnabled(newEnabled);

    try {
      await dispatch(
        updateEstimatorHook({
          website,
          enabled: newEnabled,
          clientId: newClientId || undefined, 
        })
      ).unwrap();
    } catch (err) {
      setEnabled(!!estimatorHook?.enabled);
      setClientId(estimatorHook?.clientId || "");
      toast.error("Failed to update estimator hook");
    }
  };

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <Content>
            <WelcomeText>Estimator Hook</WelcomeText>
            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                padding: isMobile ? 2 : 2.5,
                borderRadius: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <SubHeading>Set Up</SubHeading>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mr: 1, color: "#4b5b5d", fontWeight: 500 }}
                  >
                    Estimator Hook
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <Switch
                      checked={enabled}
                      onChange={handleToggle}
                      size={isMobile ? "small" : "medium"}
                      disabled={loading}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#455354",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#455354",
                          },
                      }}
                    />
                    {loading && (
                      <CircularProgress
                        size={isMobile ? 16 : 20}
                        sx={{
                          position: "absolute",
                          top: "25%",
                          right: "50%",
                          transform: "translate(-50%, -50%)",
                          color: "#455354",
                          zIndex: 1,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <FieldLabel>Enter Website Domain</FieldLabel>
              <StyledTextField
                value={website}
                onChange={handleWebsiteChange}
                onBlur={handleWebsiteBlur}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                error={websiteTouched && !!websiteError}
                helperText={
                  websiteTouched && websiteError
                    ? websiteError
                    : "e.g., https://yourwebsite.com (leave blank to allow embedding on any domain)"
                }
                disabled={loading}
              />
              <SubHeading>Embed Code:</SubHeading>
              {enabled && clientId ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <EmbedCodeContainer>
                    <EmbedCode>
                      <code>{generateEmbedCode()}</code>
                    </EmbedCode>
                    <CopyButton onClick={handleCopy} disabled={loading}>
                      {isCopied ? (
                        <CheckCircleOutlineIcon fontSize="small" />
                      ) : (
                        <ContentCopyIcon fontSize="small" />
                      )}
                    </CopyButton>
                  </EmbedCodeContainer>
                </motion.div>
              ) : (
                <Typography variant="body2" sx={{ mb: 2, color: "#757575" }}>
                  Enable the hook to generate the embed code.
                </Typography>
              )}
              <SubHeading>Setup Steps:</SubHeading>
              <Typography variant="body2" sx={{ mb: 1 }}>
                1. Enter your website domain and enable the hook.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                2. Copy the iframe code.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                3. Paste it into your website's HTML where you want the
                estimator to appear.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                4. Save and publish your site.
              </Typography>
              <Typography variant="body2">
                5. Test the embedded estimator on your live page. It will allow
                users to generate and view reports directly.
              </Typography>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default EstimatorHook;
